
import React, { useState } from 'react';
import './ProductDetail.css';
import { useApp } from '../../context/AppContext';

const ProductDetail: React.FC = () => {
  const { selectedProduct, addToCart, toggleWishlist, currentUser, products } = useApp();
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const [added, setAdded] = useState(false);

  if (!selectedProduct) return null;

  const product = selectedProduct;
  const isWishlisted = currentUser.wishlist.includes(product.id);
  const isOnSale = product.salePrice && product.salePrice < product.price;
  const currentPrice = product.salePrice || product.price;
  const sizeStock = product.sizes.find(s => s.size === selectedSize)?.stock || 0;
  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    if (!selectedSize) return;
    addToCart(product.id, selectedSize, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const sizeChart = {
    'XS': 'Extra Small (0-2)',
    'S': 'Small (4-6)',
    'M': 'Medium (8-10)',
    'L': 'Large (12-14)',
    'XL': 'Extra Large (16-18)',
    'One Size': 'One Size Fits All'
  };

  return (
    <div className="product-detail">
      <div className="detail-images">
        <div className="main-image">
          <img src={product.images[imageIndex]} alt={product.name} />
          {isOnSale && <span className="sale-tag">SALE</span>}
        </div>
        {product.images.length > 1 && (
          <div className="image-thumbs">
            {product.images.map((img, i) => (
              <button
                key={i}
                className={`thumb ${i === imageIndex ? 'active' : ''}`}
                onClick={() => setImageIndex(i)}
              >
                <img src={img} alt={`View ${i + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="detail-info">
        <div className="info-header">
          <h1 className="product-title">{product.name}</h1>
          <button 
            className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
            onClick={() => toggleWishlist(product.id)}
          >
            <svg viewBox="0 0 24 24" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
          </button>
        </div>

        <div className="rating-row">
          <div className="stars">
            {[1,2,3,4,5].map(i => (
              <svg key={i} viewBox="0 0 24 24" fill={i <= Math.round(product.rating) ? 'currentColor' : 'none'} stroke="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ))}
          </div>
          <span className="rating-text">{product.rating} ({product.reviews} reviews)</span>
          <span className="sold-text">{product.sold} sold</span>
        </div>

        <div className="price-row">
          {isOnSale ? (
            <>
              <span className="sale-price">${product.salePrice?.toFixed(2)}</span>
              <span className="original-price">${product.price.toFixed(2)}</span>
              <span className="discount-tag">-{Math.round((1 - (product.salePrice || 0) / product.price) * 100)}%</span>
            </>
          ) : (
            <span className="current-price">${product.price.toFixed(2)}</span>
          )}
        </div>

        <p className="description">{product.description}</p>

        <div className="size-section">
          <div className="section-header">
            <h3>Select Size</h3>
            <button className="size-chart-btn">Size Chart</button>
          </div>
          <div className="size-options">
            {product.sizes.map(size => (
              <button
                key={size.size}
                className={`size-btn ${selectedSize === size.size ? 'active' : ''} ${size.stock === 0 ? 'out-of-stock' : ''}`}
                onClick={() => size.stock > 0 && setSelectedSize(size.size)}
                disabled={size.stock === 0}
              >
                {size.size}
                {size.stock > 0 && size.stock <= 5 && <span className="low-stock">Only {size.stock} left</span>}
              </button>
            ))}
          </div>
          {selectedSize && <p className="size-info">{sizeChart[selectedSize as keyof typeof sizeChart]}</p>}
        </div>

        <div className="quantity-section">
          <h3>Quantity</h3>
          <div className="quantity-control">
            <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
            <span>{quantity}</span>
            <button onClick={() => setQuantity(q => Math.min(sizeStock || 10, q + 1))}>+</button>
          </div>
        </div>

        <div className="action-buttons">
          <button 
            className={`add-to-cart-btn ${added ? 'added' : ''}`}
            onClick={handleAddToCart}
            disabled={!selectedSize}
          >
            {added ? (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                Added to Cart!
              </>
            ) : (
              <>Add to Cart - ${(currentPrice * quantity).toFixed(2)}</>
            )}
          </button>
          <button className="buy-now-btn" disabled={!selectedSize}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="paypal-icon">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.16A.762.762 0 0 1 5.697 1.5h6.49c2.062 0 3.624.534 4.645 1.588.963.998 1.363 2.392 1.188 4.145l-.003.028c-.253 1.612-.916 2.886-1.97 3.787-1.046.895-2.393 1.35-4.003 1.35H9.863l-.698 4.447a.637.637 0 0 1-.63.536H7.076z"/>
            </svg>
            Buy Now with PayPal
          </button>
        </div>

        {relatedProducts.length > 0 && (
          <div className="related-section">
            <h3>You May Also Like</h3>
            <div className="related-scroll">
              {relatedProducts.map(p => (
                <div key={p.id} className="related-item">
                  <img src={p.images[0]} alt={p.name} />
                  <span className="related-name">{p.name}</span>
                  <span className="related-price">${(p.salePrice || p.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
